// ─── Route-snapped marker motion ─────────────────────────────────────────────
// A driver's position arrives as a GPS fix every couple of seconds. Drawing a
// straight line between consecutive fixes is what made the marker cut corners:
// at a roundabout or a dog-leg the two fixes sit either side of the bend, and
// the marker sails through the buildings between them.
//
// These helpers snap movement onto the route polyline we already hold, so the
// marker walks the ACTUAL road geometry between fixes and leans into bends the
// way the vehicle does. Everything works in a local flat-earth projection -
// exact to well under a metre over the few hundred metres between fixes, and far
// cheaper than spherical maths run every animation frame.

export type LL = { lat: number; lng: number };

const M_PER_DEG_LAT = 110540;
const mPerDegLng = (lat: number) => 111320 * Math.cos((lat * Math.PI) / 180);

// Flat-plane distance in metres, referenced to `ref` so the longitude scale is
// right for this latitude.
export const planarMeters = (a: LL, b: LL, ref: number): number => {
  const dx = (b.lng - a.lng) * mPerDegLng(ref);
  const dy = (b.lat - a.lat) * M_PER_DEG_LAT;
  return Math.hypot(dx, dy);
};

export interface Projection { seg: number; t: number; offM: number; alongM: number }

/**
 * Nearest point on `path` to `p`.
 *
 * `seg`/`t` locate it (segment index + 0..1 along that segment), `offM` is how
 * far off the route the fix actually was, and `alongM` is the distance from the
 * start of the path - which is what tells us whether one fix is ahead of
 * another, and therefore which slice of road lies between them.
 */
export const projectOnPath = (p: LL, path: LL[]): Projection | null => {
  if (!path || path.length < 2) return null;
  const ref = p.lat;
  const kx = mPerDegLng(ref);
  let best: Projection | null = null;
  let bestDist = Infinity;
  let cumulative = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const ax = path[i].lng * kx, ay = path[i].lat * M_PER_DEG_LAT;
    const bx = path[i + 1].lng * kx, by = path[i + 1].lat * M_PER_DEG_LAT;
    const px = p.lng * kx, py = p.lat * M_PER_DEG_LAT;
    const vx = bx - ax, vy = by - ay;
    const segLen2 = vx * vx + vy * vy;
    const segLen = Math.sqrt(segLen2);
    // Clamped to [0,1] so a fix past either end of the segment projects onto
    // that end rather than onto an imaginary extension of the road.
    const t = segLen2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / segLen2));
    const dist = Math.hypot(px - (ax + vx * t), py - (ay + vy * t));
    if (dist < bestDist) {
      bestDist = dist;
      best = { seg: i, t, offM: dist, alongM: cumulative + segLen * t };
    }
    cumulative += segLen;
  }
  return best;
};

/**
 * The route vertices strictly between two projections - i.e. the corners the
 * marker has to turn to get from one to the other. Endpoints are excluded; the
 * caller appends the real destination fix itself.
 */
export const pathBetween = (from: Projection, to: Projection, path: LL[]): LL[] => {
  const out: LL[] = [];
  for (let i = from.seg + 1; i <= to.seg; i++) out.push({ lat: path[i].lat, lng: path[i].lng });
  return out;
};

// Signed shortest angular difference a→b, in (-180, 180]. Rotating a vehicle
// icon from 350° to 10° has to be +20°, not -340° - otherwise the bike spins
// almost all the way round the compass to make a small right turn.
export const shortestTurn = (from: number, to: number): number => ((to - from + 540) % 360) - 180;

// Compass bearing a→b in degrees (0 = true north, clockwise).
export const bearingDeg = (a: LL, b: LL): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/** Total metres from `cur` through every waypoint in `queue`, in order. */
export const pathLength = (cur: LL, queue: LL[]): number => {
  let total = 0;
  let p: LL = cur;
  for (const q of queue) { total += planarMeters(p, q, cur.lat); p = q; }
  return total;
};

/**
 * Metres to walk this frame so the queue clears exactly as `msLeft` runs out.
 *
 * The deadline check is not an optimisation, it's the termination condition.
 * `remaining * dt / msLeft` is always a FRACTION of what's left, so on its own
 * the marker approaches the fix asymptotically and never arrives: it creeps by
 * ever-smaller amounts forever and the queue never drains. Once we're within a
 * frame of the deadline, take the whole remainder and land on it.
 */
export const stepFor = (cur: LL, queue: LL[], dtMs: number, msLeft: number): number => {
  const remaining = pathLength(cur, queue);
  if (msLeft <= dtMs) return remaining;
  return (remaining * dtMs) / msLeft;
};

/**
 * Walk `cur` `meters` along `queue`, consuming waypoints as they're passed.
 *
 * MUTATES both arguments - `cur` becomes the new position and `queue` loses the
 * waypoints now behind it. That's deliberate: this runs on every animation
 * frame, and returning fresh objects would allocate garbage ~60 times a second
 * for the whole length of a ride.
 *
 * Returns the bearing of the segment being walked (the direction the vehicle
 * should face), or null if nothing meaningful moved - a stationary driver's
 * icon must hold its last heading rather than spinning on GPS noise.
 */
export const advanceAlong = (cur: LL, queue: LL[], meters: number): number | null => {
  let step = meters;
  let facing: number | null = null;
  // Several short route vertices can fall inside a single frame on a tight bend,
  // so this consumes as many as the step covers rather than just one.
  //
  // Loops on `queue.length`, not on `step > 0`: a waypoint the marker has
  // already landed on has to be RETIRED even when there's no distance left to
  // spend. Gating on step alone left the arrived-at waypoint sitting in the
  // queue for ever - the position was correct, but the queue never emptied, so
  // the animator believed it was still walking and never came to rest.
  while (queue.length) {
    const next = queue[0];
    const segLen = planarMeters(cur, next, cur.lat);
    // Below half a metre the bearing is noise, not a direction.
    if (segLen > 0.5) facing = bearingDeg(cur, next);
    // ARRIVED_M covers the floating-point dust left when a step lands exactly
    // on a waypoint: the residue is ~1e-14 m, which is neither "reached" by a
    // `<=` test nor worth another frame of walking.
    if (segLen <= step || segLen < ARRIVED_M) {
      step = Math.max(0, step - segLen);
      cur.lat = next.lat; cur.lng = next.lng;
      queue.shift();
      continue;
    }
    if (step <= 0) break; // nothing left to spend on a waypoint still ahead
    const f = step / segLen;
    cur.lat += (next.lat - cur.lat) * f;
    cur.lng += (next.lng - cur.lng) * f;
    step = 0;
  }
  return facing;
};

// A millimetre - below anything GPS, the renderer or a human eye can tell apart.
const ARRIVED_M = 0.001;

